import { httpClient } from '../client';
import { logger } from '../../../utils/logger';
import { BaseEndpoint } from './base.endpoint';
import { ApiErrorHandler } from '../error.handler';
import {
  CreateRoutingConfigPayload,
  CreateRoutingRulePayload,
  RoutingConfig,
  RoutingConfigApiResponse,
  RoutingRule,
  RoutingRuleApiResponse,
  RoutingRulesApiResponse,
  UpdateRoutingConfigPayload,
  UpdateRoutingRulePayload,
} from '../../../types/phone-numbers';

class CallRoutingEndpoint extends BaseEndpoint {
  private static instance: CallRoutingEndpoint;

  private constructor() {
    super('organisations');
  }

  static getInstance(): CallRoutingEndpoint {
    if (!CallRoutingEndpoint.instance) {
      CallRoutingEndpoint.instance = new CallRoutingEndpoint();
    }
    return CallRoutingEndpoint.instance;
  }

  async getConfig(organisationId: string, phoneNumberId: number): Promise<RoutingConfig | null> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<RoutingConfigApiResponse>(
        `${this.basePath}/${organisationId}/twilio/routing/phone/${phoneNumberId}`,
        { headers }
      );

      logger.info('Routing config fetched successfully', { phoneNumberId });
      return response.data.data;
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.message?.includes('not found')) {
        return null;
      }
      logger.error('Failed to fetch routing config:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createConfig(
    organisationId: string,
    phoneNumberId: number,
    payload: CreateRoutingConfigPayload
  ): Promise<RoutingConfig> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<RoutingConfigApiResponse>(
        `${this.basePath}/${organisationId}/twilio/routing/phone/${phoneNumberId}`,
        payload,
        { headers }
      );

      logger.info('Routing config created successfully', { phoneNumberId });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create routing config:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async updateConfig(
    organisationId: string,
    phoneNumberId: number,
    payload: UpdateRoutingConfigPayload
  ): Promise<RoutingConfig> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<RoutingConfigApiResponse>(
        `${this.basePath}/${organisationId}/twilio/routing/phone/${phoneNumberId}`,
        payload,
        { headers }
      );

      logger.info('Routing config updated successfully', { phoneNumberId });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update routing config:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async deleteConfig(organisationId: string, phoneNumberId: number): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(
        `${this.basePath}/${organisationId}/twilio/routing/phone/${phoneNumberId}`,
        { headers }
      );

      logger.info('Routing config deleted successfully', { phoneNumberId });
    } catch (error) {
      logger.error('Failed to delete routing config:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async listRules(organisationId: string, phoneNumberId: number): Promise<RoutingRule[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<RoutingRulesApiResponse>(
        `${this.basePath}/${organisationId}/twilio/routing/rules/phone/${phoneNumberId}`,
        { headers }
      );

      logger.info('Routing rules fetched successfully', { phoneNumberId });
      const data = response.data.data;
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      // 404 means no rules exist yet — return empty array
      if (error?.response?.status === 404) {
        return [];
      }
      logger.error('Failed to fetch routing rules:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async createRule(
    organisationId: string,
    phoneNumberId: number,
    payload: CreateRoutingRulePayload
  ): Promise<RoutingRule> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<RoutingRuleApiResponse>(
        `${this.basePath}/${organisationId}/twilio/routing/rules/phone/${phoneNumberId}`,
        payload,
        { headers }
      );

      logger.info('Routing rule created successfully', { phoneNumberId });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to create routing rule:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async updateRule(
    organisationId: string,
    ruleId: number,
    payload: UpdateRoutingRulePayload
  ): Promise<RoutingRule> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.put<RoutingRuleApiResponse>(
        `${this.basePath}/${organisationId}/twilio/routing/rules/${ruleId}`,
        payload,
        { headers }
      );

      logger.info('Routing rule updated successfully', { ruleId });
      return response.data.data;
    } catch (error) {
      logger.error('Failed to update routing rule:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async deleteRule(organisationId: string, ruleId: number): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.basePath}/${organisationId}/twilio/routing/rules/${ruleId}`, {
        headers,
      });

      logger.info('Routing rule deleted successfully', { ruleId });
    } catch (error) {
      logger.error('Failed to delete routing rule:', error);
      return ApiErrorHandler.handle(error);
    }
  }

  async triggerSync(organisationId: string, phoneNumberId: number): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.post(
        `${this.basePath}/${organisationId}/twilio/routing/sync/phone/${phoneNumberId}`,
        {},
        { headers }
      );

      logger.info('Routing sync triggered successfully', { phoneNumberId });
    } catch (error) {
      logger.error('Failed to trigger routing sync:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const callRoutingEndpoint = CallRoutingEndpoint.getInstance();
